import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export function Pagination({
  currentPage,
  totalPages,
  totalCount,
  itemsPerPage,
  onPageChange,
}: PaginationProps) {
  
  if (totalPages <= 1) return null;

  const from = (currentPage - 1) * itemsPerPage + 1;
  const to = Math.min(currentPage * itemsPerPage, totalCount);

  const getPageNumbers = (delta = 2): number[] => {
    const pages: number[] = [];
    for (
      let i = Math.max(1, currentPage - delta);
      i <= Math.min(totalPages, currentPage + delta);
      i++
    ) {
      pages.push(i);
    }
    return pages;
  };

  const navBtn =
    'p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-brand-50 hover:border-brand-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-1 py-4 border-t border-gray-100">
      {/* Count info */}
      <p className="text-sm text-gray-500 order-2 sm:order-1">
        Showing{' '}
        <span className="font-semibold text-gray-800">{from}–{to}</span> of{' '}
        <span className="font-semibold text-gray-800">{totalCount}</span> items
      </p>

      {/* Controls */}
      <div className="flex items-center gap-1.5 order-1 sm:order-2">
        {/* First */}
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className={navBtn}
          aria-label="First page"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>

        {/* Prev */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={navBtn}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Page numbers — desktop */}
        <div className="hidden sm:flex items-center gap-1">
          {getPageNumbers(2).map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`min-w-[36px] h-9 px-2 rounded-lg border text-sm font-semibold transition-colors ${
                page === currentPage
                  ? 'bg-brand-500 border-brand-500 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-brand-50 hover:border-brand-300'
              }`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Page info — mobile */}
        <span className="sm:hidden text-sm font-semibold text-gray-700 px-2">
          {currentPage} / {totalPages}
        </span>

        {/* Next */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={navBtn}
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Last */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className={navBtn}
          aria-label="Last page"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
